import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  AISpecificHeadersSchema,
  BooleanStrict,
  ErrorGenericSchema,
  GenericUserID,
  GetMemoriesQueryParamsSchema,
  MemoryConstructionParamsSchema,
  MemoryForAISchema,
  MemoryGroupForAISchema,
  ResponseMetaSchema,
} from '../../models';

extendZodWithOpenApi(z);
const c = initContract();

export const apiAiMemoriesRoute = c.router({
  getMemoryQueriesAndToolConfigsForAi: {
    method: 'GET',
    path: `/1.0/ai/memory/query_filters`,
    summary: 'AI-only endpoint for retrieving Memory Queries, Filters, and Tool Configs',
    description:
      'These queries, filters & configs are used to construct the Memory/Groups for display on timeline/list/etc.',
    query: z.object({
      ticUserId: GenericUserID.describe('The TIC `user.id` owning the query/filter/configs.'),
    }),
    responses: {
      200: MemoryConstructionParamsSchema.omit({ startDate: true, endDate: true }),
    },
    headers: AISpecificHeadersSchema,
  },
  getMemoriesForAiFirehose: {
    method: 'GET',
    path: `/1.0/ai/memories`,
    summary: 'AI-only endpoints for retrieving Memories, for training',
    description:
      "Returns Memories in a given date range. Formatter for AI firehose, processed via user's view preferences. Only used internally. Note: `query.page`, `query.limit`, `meta.count` and `meta.totalCount` apply to individual `memory` records in the database, not the just-in-time created `memoryGroups` or combined `memories` in response.",
    query: GetMemoriesQueryParamsSchema.extend({
      timelyUserId: z.coerce
        .number()
        .int()
        .optional()
        .describe('The Timely `user.id` owning the Memories, if TIC user.id is not known.'),
      ticUserId: GenericUserID.optional().describe('The TIC `user.id` owning the Memories.'),
      applyGroupingLogic: BooleanStrict.default(true).describe(
        'Apply the users grouping preferences on the Memories, or default settings if not customized by end-user. When enabled `memoryGroups` will return, otherwise `memories` is returned as a flat list'
      ),
    }),
    responses: {
      200: z.object({
        memories: z.array(MemoryForAISchema).optional(),
        memoryGroups: z.array(MemoryGroupForAISchema).optional(),
        meta: ResponseMetaSchema.pick({ count: true, totalCount: true, message: true }),
      }),
      504: ErrorGenericSchema.describe(
        'The memories query exceeded its statement timeout and was canceled server-side. ' +
          'Carries `Retry-After`; narrow the date range before retrying.'
      ),
    },
    headers: AISpecificHeadersSchema,
  },
});
